import React from 'react';
import {
  LanguageCode,
  NewsItem,
  EventItem,
  ActivityItem,
  CultureItem,
  MediaGalleryItem,
  SiteSettings,
  HeroSlide,
} from '../types';
import { translations } from '../i18n/translations';
import {
  Newspaper,
  Calendar,
  Layers,
  BookOpen,
  Tv,
  UserCheck,
  HeartHandshake,
  Image as ImageIcon,
  FileText,
  Phone,
  Info,
  User,
  ChevronRight,
} from 'lucide-react';

interface HomeViewProps {
  currentLang: LanguageCode;
  heroSlides?: HeroSlide[];
  news?: NewsItem[];
  events?: EventItem[];
  activities?: ActivityItem[];
  cultureItems?: CultureItem[];
  gallery?: MediaGalleryItem[];
  settings: SiteSettings;
  onNavClick: (tab: string) => void;
  onOpenNewsDetail?: (news: NewsItem) => void;
  onOpenEventDetail?: (evt: EventItem) => void;
}

export const HomeView: React.FC<HomeViewProps> = ({
  currentLang,
  news = [],
  events = [],
  activities = [],
  onNavClick,
}) => {
  const t = translations[currentLang];

  const quickServices = [
    {
      id: 'news',
      label: t.news || 'HABERLER',
      icon: Newspaper,
      badge: news.length > 0 ? `${news.length}` : '2',
      badgeBg: 'bg-[#38BDF8] text-[#0B1426] shadow-[0_0_10px_rgba(56,189,248,0.7)]',
    },
    {
      id: 'events',
      label: t.events || 'ETKİNLİKLER',
      icon: Calendar,
      badge: events.length > 0 ? `${events.length}` : '2',
      badgeBg: 'bg-[#38BDF8] text-[#0B1426] shadow-[0_0_10px_rgba(56,189,248,0.7)]',
    },
    {
      id: 'activities',
      label: t.activities || 'FAALİYETLER',
      icon: Layers,
      badge: activities.length > 0 ? `${activities.length}` : '3',
      badgeBg: 'bg-[#38BDF8] text-[#0B1426] shadow-[0_0_10px_rgba(56,189,248,0.7)]',
    },
    {
      id: 'culture',
      label: t.culture || 'KÜRT KÜLTÜRÜ',
      icon: BookOpen,
    },
    {
      id: 'live',
      label: t.liveStream || 'CANLI YAYIN',
      icon: Tv,
      badge: t.liveNow || 'ŞİMDİ CANLI',
      badgeBg: 'bg-red-600 text-white font-black border border-red-400/80 shadow-[0_0_10px_rgba(239,68,68,0.8)] animate-pulse',
    },
    {
      id: 'membership',
      label: t.membership || 'ÜYELİK',
      icon: UserCheck,
    },
    {
      id: 'donate',
      label: t.donate || 'DESTEK OL',
      icon: HeartHandshake,
    },
    {
      id: 'gallery',
      label: t.gallery || 'GALERİ',
      icon: ImageIcon,
    },
    {
      id: 'documents',
      label: t.documents || 'BELGELER',
      icon: FileText,
    },
    {
      id: 'contact',
      label: t.contact || 'İLETİŞİM',
      icon: Phone,
    },
    {
      id: 'about',
      label: t.aboutUs || 'DERNEĞİMİZ',
      icon: Info,
    },
    {
      id: 'member-panel',
      label: t.memberPanel || 'ÜYE PANELİ',
      icon: User,
    },
  ];

  return (
    <div className="w-full h-full flex flex-col justify-center items-center py-2 select-none overflow-hidden my-auto">
      {/* 3 Columns x 4 Rows Clean Grid - Fits entire screen without scrolling */}
      <div className="w-full max-w-4xl grid grid-cols-3 gap-2 sm:gap-3.5 px-1 sm:px-2">
        {quickServices.map((srv) => {
          const IconComponent = srv.icon;
          return (
            <button
              key={srv.id}
              onClick={() => onNavClick(srv.id)}
              className="group relative p-3 sm:p-4 rounded-2xl bg-[#0B1426]/90 border border-[#38BDF8]/40 hover:border-[#38BDF8] hover:bg-[#132238] active:bg-[#38BDF8] active:border-[#A5F3FC] active:scale-95 transition-all duration-300 flex flex-col justify-between items-start text-left shadow-lg hover:shadow-[0_0_20px_rgba(56,189,248,0.5)] active:shadow-[0_0_30px_rgba(165,243,252,0.9)] overflow-hidden min-h-[82px] sm:min-h-[110px] cursor-pointer"
            >
              {/* Top Row: Icon + Badge/Chevron */}
              <div className="w-full flex items-center justify-between">
                <div className="p-1.5 sm:p-2 rounded-xl bg-[#132238] border border-[#38BDF8]/30 text-[#38BDF8] group-hover:bg-[#38BDF8] group-hover:text-[#0B1426] group-hover:border-[#A5F3FC] group-hover:shadow-[0_0_12px_rgba(56,189,248,0.8)] transition-all shadow-inner">
                  <IconComponent className="w-4 h-4 sm:w-5 sm:h-5" />
                </div>

                {srv.badge ? (
                  <span
                    className={`px-1.5 py-0.5 rounded-full text-[9px] sm:text-[10px] font-black ${srv.badgeBg}`}
                  >
                    {srv.badge}
                  </span>
                ) : (
                  <ChevronRight className="w-3.5 h-3.5 text-[#38BDF8]/60 group-hover:text-[#A5F3FC] transition-colors" />
                )}
              </div>

              {/* Bottom Label */}
              <div className="w-full mt-2">
                <p className="text-[11px] sm:text-xs font-black text-white group-hover:text-[#38BDF8] transition-colors tracking-tight leading-tight uppercase line-clamp-2">
                  {srv.label}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
